// src/App.jsx
import React, { Suspense, lazy } from "react";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from "react-router-dom";

// --- Import Core Providers (needed globally) ---
import { AuthProvider } from "./contexts/AuthContext"; // Adjust path if needed
import { ThemeProvider } from "./components/ThemeProvider"; // Adjust path if needed

// --- Import Layouts ---
import PublicLayout from "./layouts/PublicLayout"; // Adjust path if needed
import AppLayout from "./layouts/AppLayout"; // Adjust path if needed

// --- Import General Components ---
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";

// --- Eagerly Imported PUBLIC Route Components ---
import Login from "./routes/Login.jsx";
import PublicLandingPage from "./routes/PublicLandingPage";
import RequestPasswordResetForm from "./routes/RequestPasswordResetForm";
import ResetPasswordFrom from "./routes/ResetPasswordFrom"; // Ensure filename is correct
import Plans from "./routes/Plans";

// --- Lazy Loaded Route Components (ALL from old example) ---
const Calendar = lazy(() => import("./components/calendar/Calendar.jsx")); // Note: Removed complex preload from old example for clarity, can be added back if needed
const Settings = lazy(() => import("./routes/Settings.jsx"));
const Admin = lazy(() => import("./routes/Admin.jsx"));
const CreateTrialUser = lazy(() => import("./components/CreateTrialUser")); // For /signup/trial
const CreateUser = lazy(() => import("./components/CreateUser")); // For /signup
const UserAccount = lazy(() =>
  import("./components/UserAccountInfo/UserAccount")
);
const Support = lazy(() => import("./routes/Support"));
const Payment = lazy(() => import("./routes/Payment"));
const PaymentAccount = lazy(() => import("./routes/PaymentAccount"));
const CompletedTasksViewer = lazy(() =>
  import("./components/calendar/CompletedTasksViewer/CompletedTasksViewer")
);
const ProjectTimeline = lazy(() => import("./components/TimeLine/TimeLine"));
const Qcalendar = lazy(() => import("./components/Qcalendar/Qcalendar"));
const PublicSchedulePage = lazy(() =>
  import("./components/Qcalendar/PublicSchedulePage")
);
const BookingConfirmationPage = lazy(() =>
  import("./components/Qcalendar/BookingConfirmationPage")
);

// --- Suspense Fallback Component ---
// (Using the simpler version from previous successful builds, adjust if needed)
const ChunkLoadingFallback = ({ componentName }) => {
  return (
    <div
      style={{
        padding: "20px",
        textAlign: "center",
        height: "calc(100vh - 60px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div>Loading {componentName || "feature"}...</div>
    </div>
  );
};

// --- Error Fallback Component (Example Definition) ---
function GlobalErrorFallback({ error }) {
  console.error("Global Error Boundary Caught:", error);
  // Your fallback UI
  return <div>Global Application Error Occurred.</div>;
}

// --- Router Configuration ---
const router = createBrowserRouter([
  {
    // --- Public Routes Group ---
    element: <PublicLayout />,
    children: [
      // Eager Public Routes
      { path: "/", element: <PublicLandingPage />, index: true },
      { path: "login", element: <Login /> },
      { path: "plans", element: <Plans /> },
      // Ensure path matches old example: /request-pw-reset
      { path: "request-pw-reset", element: <RequestPasswordResetForm /> },
      { path: "reset-password", element: <ResetPasswordFrom /> },

      // Lazy Public Routes (Signup - No ProtectedRoute as per old example)
      {
        path: "signup/trial",
        element: (
          <Suspense
            fallback={<ChunkLoadingFallback componentName="Trial Signup" />}
          >
            <CreateTrialUser />
          </Suspense>
        ),
      },
      {
        path: "signup",
        element: (
          <Suspense fallback={<ChunkLoadingFallback componentName="Signup" />}>
            <CreateUser />
          </Suspense>
        ),
      },
      {
        path: "PublicSchedulePage",
        element: (
          <Suspense
            fallback={
              <ChunkLoadingFallback componentName="PublicSchedulePage" />
            }
          >
            <PublicSchedulePage />
          </Suspense>
        ),
      },
    ],
  },
  {
    // --- Protected/Application Routes Group ---
    element: <AppLayout />,
    children: [
      // Protected & Lazy Routes (ALL from old example)
      {
        path: "journal",
        element: (
          <ProtectedRoute>
            <Suspense
              fallback={<ChunkLoadingFallback componentName="Calendar" />}
            >
              <Calendar />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "admin",
        element: (
          <ProtectedRoute>
            {" "}
            {/* Add requiredRole="admin" if needed */}
            <Suspense
              fallback={<ChunkLoadingFallback componentName="Admin Area" />}
            >
              <Admin />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "settings",
        element: (
          <ProtectedRoute>
            <Suspense
              fallback={<ChunkLoadingFallback componentName="Settings" />}
            >
              <Settings />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "account",
        element: (
          <ProtectedRoute>
            <Suspense
              fallback={<ChunkLoadingFallback componentName="User Account" />}
            >
              <UserAccount />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "support",
        element: (
          <ProtectedRoute>
            <Suspense
              fallback={<ChunkLoadingFallback componentName="Support" />}
            >
              <Support />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "payment",
        element: (
          <ProtectedRoute>
            <Suspense
              fallback={<ChunkLoadingFallback componentName="Payment" />}
            >
              <Payment />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Note exact path casing from old example
      {
        path: "PaymentAccount",
        element: (
          <ProtectedRoute>
            <Suspense
              fallback={
                <ChunkLoadingFallback componentName="Payment Account" />
              }
            >
              <PaymentAccount />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Note exact path casing from old example
      {
        path: "CompletedTasksViewer",
        element: (
          <ProtectedRoute>
            <Suspense
              fallback={
                <ChunkLoadingFallback componentName="Completed Tasks" />
              }
            >
              <CompletedTasksViewer />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "timeline",
        element: (
          <ProtectedRoute>
            <Suspense
              fallback={
                <ChunkLoadingFallback componentName="Project Timeline" />
              }
            >
              <ProjectTimeline />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "Qcalendar",
        element: (
          <ProtectedRoute>
            <Suspense
              fallback={<ChunkLoadingFallback componentName="Qcalendar" />}
            >
              <Qcalendar />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "BookingConfirmationPage",
        element: (
          <ProtectedRoute>
            <Suspense
              fallback={
                <ChunkLoadingFallback componentName="BookingConfirmationPage" />
              }
            >
              <BookingConfirmationPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },
  // --- Global Fallback Route ---
  { path: "*", element: <Navigate to="/" replace /> },
]);

// --- Main App Component ---
function App() {
  return (
    <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
      <ThemeProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
